import toInteger from 'lodash/toInteger';
import React, { useEffect, useRef, useState } from 'react';
import { imageKit } from '@meitu/wasm-lib';
import { BaseLayerView } from '@/framework';
import { loadImageAsync } from '@/utils/file-kit';
import { toFixedNumber } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IMAGE_LAYER_CANVAS_ID_PREFIX, IRenderLayerProps } from '@/version-types';

export const MainLayerRenderWrapper: React.FC<{
    layer: ILayerProps;
    isNavigator: boolean;
    children?: React.ReactNode;
}> = ({ layer, isNavigator, children }) => {
    const {
        naturalHeight,
        naturalWidth,
        imageTransform: {
            widthRatio,
            heightRatio,
            flipH,
            flipV,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
            scale,
            rotate,
        },
    } = layer.data as IImageLayerProps;

    const imageInnerStyle: React.CSSProperties = {
        width: `${naturalWidth * toFixedNumber(scale, 3)}px`,
        height: `${naturalHeight * toFixedNumber(scale, 3)}px`,
        transformOrigin: 'center center',
    };

    const imageWrapStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
    };

    const flipStyle: React.CSSProperties = {
        pointerEvents: 'none',
    };

    // 翻转
    if (flipH) {
        flipStyle.transform = 'scaleX(-1) ';
    }

    if (flipV) {
        const ry180 = 'scaleY(-1)';
        flipStyle.transform = flipStyle.transform ? flipStyle.transform + ry180 : ry180;
    }

    const newLayer = { ...layer, width: naturalWidth * scale, height: naturalHeight * scale };

    return (
        <BaseLayerView layer={newLayer} isNavigator={isNavigator} disableEvents={true}>
            <div style={{ ...imageWrapStyle }}>
                <div style={flipStyle}>
                    <div style={imageInnerStyle}>{children}</div>
                </div>
            </div>
        </BaseLayerView>
    );
};

const MainLayer: React.FC<IRenderLayerProps> = ({ layer, isNavigator, enterLoading, exitLoading }) => {
    const mainCanvas = useRef<HTMLCanvasElement>(null);
    const { url, naturalHeight, naturalWidth } = layer.data as IImageLayerProps;
    // 渲染gl画布
    const [showEnhanceCanvas, setShowEnhanceCanvas] = useState(false);

    const renderEnhanceCanvas = layer.__renderEnhanceCanvas;
    const isUseNavigatorRender = layer.__useNavigatorRender;
    const latestURLRef = useRef('');

    useEffect(() => {
        if ((!isNavigator && !isUseNavigatorRender) || (isNavigator && isUseNavigatorRender)) {
            if (renderEnhanceCanvas) {
                const glCanvas = imageKit.getGLCanvas();
                if (glCanvas) {
                    glCanvas.removeAttribute('style');
                    glCanvas.style.width = `100%`;
                    glCanvas.style.height = `100%`;
                    document.getElementById('GLContainer')?.appendChild(glCanvas);
                }
                mainCanvas.current?.setAttribute('ready', 'false');
                setShowEnhanceCanvas(true);
                return;
            }
        }

        // 滤镜预览并且不是导航器的图层不需要重新加载
        if (layer.__filterPreview && ((!isNavigator && !isUseNavigatorRender) || (isNavigator && isUseNavigatorRender)))
            return;
        mainCanvas.current?.setAttribute('ready', 'false');
        enterLoading?.(layer.id);
        latestURLRef.current = url;
        loadImageAsync(url, true)
            .then((img) => {
                // HACK: 多次变化 url 时，异步加载的时序问题导致 canvas 绘制的是之前的图片
                if (latestURLRef.current !== url) {
                    return;
                }
                const width = naturalWidth;
                const height = naturalHeight;
                const ctx = mainCanvas.current?.getContext('2d');
                ctx?.clearRect(0, 0, width, height);
                mainCanvas.current?.setAttribute('width', `${width}px`);
                mainCanvas.current?.setAttribute('height', `${height}px`);
                ctx?.drawImage(img, 0, 0, width, height);
                mainCanvas.current?.setAttribute('ready', 'true');
                setShowEnhanceCanvas(false);
                exitLoading?.(layer.id);
            })
            .catch(() => {
                exitLoading?.(layer.id);
            });
    }, [naturalWidth, naturalHeight, url, renderEnhanceCanvas, layer.__filterPreview, isUseNavigatorRender]);

    return (
        <MainLayerRenderWrapper layer={layer} isNavigator={isNavigator}>
            <div
                id={
                    (isNavigator && !layer.__useNavigatorRender) || (!isNavigator && layer.__useNavigatorRender)
                        ? ''
                        : 'GLContainer'
                }
                style={{
                    display: showEnhanceCanvas ? 'block' : 'none',
                }}
            ></div>
            <div
                style={{
                    opacity: showEnhanceCanvas ? 0 : 1,
                }}
            >
                <canvas
                    id={
                        (isNavigator && !layer.__useNavigatorRender) || (!isNavigator && layer.__useNavigatorRender)
                            ? ''
                            : `${IMAGE_LAYER_CANVAS_ID_PREFIX}${layer.id}`
                    }
                    ref={mainCanvas}
                    style={{ width: '100%', height: '100%' }}
                ></canvas>
            </div>
        </MainLayerRenderWrapper>
    );
};

export default React.memo(MainLayer);
